import React, { useState } from "react";
import {
  IconButton,
  Typography,
  Box,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import styles from "./outcome-modifier.module.scss";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  setOutcomeModifierFeedback,
  resetOutcomeModifierFeedback,
} from "../../../store/filtersSlice";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import MarkdownRenderer from "../../MarkdownRenderer";

interface ContractDetails {
  contract_type: string;
  contracting_parties: string[];
  agreement_date: string;
}

interface DocumentOutcome {
  document_name: string;
  contract_details: ContractDetails;
  trigger_result: string;
  action_required: string | string[];
  consequence_summary: string;
  risk_tier: string;
}

interface ParsedContent {
  finalAnswer?: {
    documents?: DocumentOutcome[];
  };
  question?: string;
}

interface OutcomeModifierAgentProps {
  parsedContent: ParsedContent;
  messageId?: string;
}

const OUTCOME_MODIFIER_ENDPOINT = "/stream/coc-review";

const getRiskTierColor = (riskTier: string): string => {
  const normalized = riskTier?.toLowerCase();
  if (normalized === "green" || normalized === "low") return "green";
  if (normalized === "red" || normalized === "high") return "red";
  if (normalized === "yellow" || normalized === "medium") return "medium";
  return "medium";
};

const formatContractingParties = (parties: string[]): string => {
  if (!parties || parties.length === 0) return "N/A";
  if (parties.length === 1) return parties[0];
  if (parties.length === 2) return `${parties[0]} and ${parties[1]}`;
  return `${parties.slice(0, -1).join(", ")}, and ${parties[parties.length - 1]}`;
};

export default function OutcomeModifierAgent({
  parsedContent,
  messageId,
}: OutcomeModifierAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const documents = parsedContent?.finalAnswer?.documents || [];

  const feedback = useSelector((state: RootState) =>
    messageId ? state.filters.outcomeModifierFeedback[messageId] : undefined,
  );
  const accept_array: any[] = feedback?.accept_array ?? [];
  const modify_array: any[] = feedback?.modify_array ?? [];

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedValues, setEditedValues] = useState<Partial<DocumentOutcome>>(
    {},
  );

  const isLatestMessage =
    [...currentChat].reverse().find((m) => m.sender === "agent")?.id ===
    messageId;

  const getDocumentKey = (index: number) => `doc_${index}`;

  const getDocumentStatus = (index: number) => {
    const key = getDocumentKey(index);
    if (accept_array.some((d) => d._key === key)) return "accepted";
    if (modify_array.some((d) => d._key === key)) return "modified";
    return null;
  };

  const handleEdit = (doc: DocumentOutcome, index: number) => {
    setEditingIndex(index);
    setEditedValues({
      trigger_result: doc.trigger_result,
      action_required: Array.isArray(doc.action_required)
        ? doc.action_required.join("\n")
        : doc.action_required,
      consequence_summary: doc.consequence_summary,
      risk_tier: doc.risk_tier,
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedValues({});
  };

  const handleSaveEdit = (doc: DocumentOutcome, index: number) => {
    const key = getDocumentKey(index);

    // Parse action_required back to array if it was edited
    const parsedActionRequired =
      typeof editedValues.action_required === "string"
        ? editedValues.action_required
            .split("\n")
            .map((item) => item.trim())
            .filter(Boolean)
        : editedValues.action_required;

    const modifiedDoc = {
      ...doc,
      trigger_result: editedValues.trigger_result || doc.trigger_result,
      action_required: parsedActionRequired || doc.action_required,
      consequence_summary:
        editedValues.consequence_summary || doc.consequence_summary,
      risk_tier: editedValues.risk_tier || doc.risk_tier,
      _key: key,
    };

    // Check if values actually changed
    const hasChanges =
      editedValues.trigger_result !== doc.trigger_result ||
      JSON.stringify(parsedActionRequired) !==
        JSON.stringify(doc.action_required) ||
      editedValues.consequence_summary !== doc.consequence_summary ||
      editedValues.risk_tier !== doc.risk_tier;

    if (!messageId) return;

    if (hasChanges) {
      // Modified
      dispatch(
        setOutcomeModifierFeedback({
          messageId,
          feedback: {
            modify_array: [
              ...modify_array.filter((d) => d._key !== key),
              modifiedDoc,
            ],
            accept_array: accept_array.filter((d) => d._key !== key),
          },
        }),
      );
    } else {
      // Accepted (no changes)
      dispatch(
        setOutcomeModifierFeedback({
          messageId,
          feedback: {
            accept_array: [
              ...accept_array.filter((d) => d._key !== key),
              { ...doc, _key: key },
            ],
            modify_array: modify_array.filter((d) => d._key !== key),
          },
        }),
      );
    }

    setEditingIndex(null);
    setEditedValues({});
  };

  const handleAccept = (doc: DocumentOutcome, index: number) => {
    if (!messageId) return;
    const key = getDocumentKey(index);

    // Accept the document as-is
    dispatch(
      setOutcomeModifierFeedback({
        messageId,
        feedback: {
          accept_array: [
            ...accept_array.filter((d) => d._key !== key),
            { ...doc, _key: key },
          ],
          modify_array: modify_array.filter((d) => d._key !== key),
        },
      }),
    );
  };

  const handleUndo = (index: number) => {
    if (!messageId) return;
    const key = getDocumentKey(index);

    // Remove from both arrays (undo the decision)
    dispatch(
      setOutcomeModifierFeedback({
        messageId,
        feedback: {
          accept_array: accept_array.filter((d) => d._key !== key),
          modify_array: modify_array.filter((d) => d._key !== key),
        },
      }),
    );
  };

  const handleApplyChanges = () => {
    if (!messageId) return;

    const decisionsText = [
      accept_array.length > 0
        ? `Accepted: ${accept_array.length} document(s)`
        : "",
      modify_array.length > 0
        ? `Modified: ${modify_array.length} document(s)`
        : "",
    ]
      .filter(Boolean)
      .join(". ");

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || 11,
        message: decisionsText || "Applied changes",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: OUTCOME_MODIFIER_ENDPOINT,
        sessionId: null,
        accept_array: accept_array,
        modify_array: modify_array,
      }),
    );
    dispatch(resetOutcomeModifierFeedback(messageId));
  };

  if (documents.length === 0) {
    return (
      <div className={styles.container}>
        <p>No outcome data available.</p>
      </div>
    );
  }

  const totalDecisions = accept_array.length + modify_array.length;

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <h2 className={styles.title}>Change of Control Outcome</h2>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Contract Details</th>
              <th>Identify CoC Trigger</th>
              <th>Action Items</th>
              <th>Consequence of not adhering to CoC requirements</th>
              <th>Risk Tier</th>
              {isLatestMessage && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {documents.map((doc, index) => {
              const isEditing = editingIndex === index;
              const status = getDocumentStatus(index);
              const rowClass =
                status === "accepted"
                  ? styles.acceptedRow
                  : status === "modified"
                    ? styles.modifiedRow
                    : "";

              return (
                <tr key={index} className={rowClass}>
                  <td className={styles.documentName}>
                    {doc.contract_details ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <div>
                          <strong
                            style={{ color: "#00a888", fontSize: "0.85em" }}
                          >
                            Type:
                          </strong>{" "}
                          <span>{doc.contract_details.contract_type}</span>
                        </div>
                        <div>
                          <strong
                            style={{ color: "#00a888", fontSize: "0.85em" }}
                          >
                            Parties:
                          </strong>{" "}
                          <span>
                            {formatContractingParties(
                              doc.contract_details.contracting_parties,
                            )}
                          </span>
                        </div>
                        <div>
                          <strong
                            style={{ color: "#00a888", fontSize: "0.85em" }}
                          >
                            Date:
                          </strong>{" "}
                          <span>{doc.contract_details.agreement_date}</span>
                        </div>
                      </div>
                    ) : (
                      doc.document_name
                    )}
                  </td>
                  <td className={styles.editableCell}>
                    {isEditing ? (
                      <Select
                        fullWidth
                        size="small"
                        value={
                          editedValues.trigger_result ??
                          doc.trigger_result ??
                          ""
                        }
                        onChange={(e) =>
                          setEditedValues({
                            ...editedValues,
                            trigger_result: e.target.value,
                          })
                        }
                        sx={{
                          fontSize: "13px",
                          "& .MuiSelect-select": {
                            padding: "8px 14px",
                          },
                        }}
                      >
                        <MenuItem value="Triggered">Triggered</MenuItem>
                        <MenuItem value="Not triggered">Not triggered</MenuItem>
                        <MenuItem value="Review required">
                          Review required
                        </MenuItem>
                      </Select>
                    ) : (
                      <span>
                        {status === "modified"
                          ? modify_array.find(
                              (d) => d._key === getDocumentKey(index),
                            )?.trigger_result || doc.trigger_result
                          : doc.trigger_result}
                      </span>
                    )}
                  </td>
                  <td className={styles.actionRequired}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        size="small"
                        value={editedValues.action_required}
                        onChange={(e) =>
                          setEditedValues({
                            ...editedValues,
                            action_required: e.target.value,
                          })
                        }
                        placeholder="One action per line"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: "13px",
                          },
                        }}
                      />
                    ) : (
                      (() => {
                        const actionReq =
                          status === "modified"
                            ? modify_array.find(
                                (d) => d._key === getDocumentKey(index),
                              )?.action_required || doc.action_required
                            : doc.action_required;
                        return Array.isArray(actionReq) ? (
                          <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                            {actionReq.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        ) : (
                          actionReq
                        );
                      })()
                    )}
                  </td>
                  <td className={styles.consequenceSummary}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        size="small"
                        value={editedValues.consequence_summary}
                        onChange={(e) =>
                          setEditedValues({
                            ...editedValues,
                            consequence_summary: e.target.value,
                          })
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: "13px",
                          },
                        }}
                      />
                    ) : (
                      <span>
                        {status === "modified"
                          ? modify_array.find(
                              (d) => d._key === getDocumentKey(index),
                            )?.consequence_summary || doc.consequence_summary
                          : doc.consequence_summary || "N/A"}
                      </span>
                    )}
                  </td>
                  <td className={styles.centered}>
                    {isEditing ? (
                      <Select
                        size="small"
                        value={editedValues.risk_tier || doc.risk_tier}
                        onChange={(e) =>
                          setEditedValues({
                            ...editedValues,
                            risk_tier: e.target.value,
                          })
                        }
                        sx={{
                          minWidth: 100,
                          fontSize: "13px",
                          "& .MuiSelect-select": {
                            padding: "8px 14px",
                          },
                        }}
                      >
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Low">Low</MenuItem>
                      </Select>
                    ) : (
                      <span
                        className={`${styles.riskBadge} ${
                          styles[
                            getRiskTierColor(
                              status === "modified"
                                ? modify_array.find(
                                    (d) => d._key === getDocumentKey(index),
                                  )?.risk_tier || doc.risk_tier
                                : doc.risk_tier,
                            )
                          ]
                        }`}
                      ></span>
                    )}
                  </td>
                  {isLatestMessage && (
                    <td className={styles.actionsCell}>
                      <Box className={styles.actionsWrapper}>
                        {isEditing ? (
                          <>
                            <Tooltip title="Save">
                              <IconButton
                                size="small"
                                onClick={() => handleSaveEdit(doc, index)}
                                className={styles.saveButton}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                className={styles.cancelButton}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(doc, index)}
                                className={styles.editButton}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {status === "accepted" ? (
                              <>
                                <Box className={styles.statusBadge}>
                                  <CheckCircleIcon sx={{ fontSize: 16, color: "#28a745" }} />
                                  <Typography className={styles.statusText}>
                                    Accepted
                                  </Typography>
                                </Box>
                                <Tooltip title="Undo">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUndo(index)}
                                    className={styles.undoButton}
                                  >
                                    <UndoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : status === "modified" ? (
                              <>
                                <Box className={`${styles.statusBadge} ${styles.modifiedBadge}`}>
                                  <CheckCircleIcon sx={{ fontSize: 16, color: "#ffc107" }} />
                                  <Typography className={`${styles.statusText} ${styles.modifiedText}`}>
                                    Modified
                                  </Typography>
                                </Box>
                                <Tooltip title="Undo">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUndo(index)}
                                    className={styles.undoButton}
                                  >
                                    <UndoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleAccept(doc, index)}
                                className={styles.acceptButton}
                              >
                                Accept
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isLatestMessage && totalDecisions > 0 && (
        <Box className={styles.applySection}>
          <Box className={styles.decisionSummary}>
            <Typography className={styles.decisionSummaryText}>
              {accept_array.length > 0 && `${accept_array.length} accepted`}
              {accept_array.length > 0 && modify_array.length > 0 && ", "}
              {modify_array.length > 0 && `${modify_array.length} modified`}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleApplyChanges}
            className={styles.applyButton}
          >
            Apply Changes & Continue
          </Button>
        </Box>
      )}

      {/* Question Section */}
      {parsedContent?.question && (
        <Box className={styles.questionBox}>
          <Box className={styles.questionContent}>
            <MarkdownRenderer content={parsedContent.question} />
          </Box>
        </Box>
      )}
    </div>
  );
}
